import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import { getAuthorazieHeader } from 'service/service';
import { url, endpoints } from 'service/config';
import axios from 'axios';

interface DashboardConfig {
	_id: string;
	dashboardName: string;
	slug: string;
	priority: number;
	status: string;
	url: string;
	urlType: 'iframe' | 'external' | 'redirect' | 'internal';
}

const XOpsIntelligenceHost: React.FC = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const [controller] = usePepwiseAuthController();
	const { capabilities } = controller;

	const dashboard = location.state?.dashboard as DashboardConfig | undefined;

	const [dashboardUrl, setDashboardUrl] = useState<string>('');
	const [loading, setLoading] = useState<boolean>(true);
	const [iframeError, setIframeError] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);

	const isEntitled = (capabilities ?? []).includes('Data Ops');

	const getFullUrl = (dashboardUrl: string) => {
		if (/^https?:\/\//i.test(dashboardUrl)) {
			return dashboardUrl;
		}

		return `${window.location.origin}/${dashboardUrl.replace(/^\/+/, '')}`;
	};

	useEffect(() => {
		if (!isEntitled) return;

		const loadDashboard = async () => {
			try {
				setLoading(true);
				setError(null);
				setIframeError(false);
				setDashboardUrl('');

				if (
					!dashboard ||
					dashboard.status?.toLowerCase() !== 'active' ||
					dashboard.urlType === 'redirect'
				) {
					setError('Dashboard not found.');
					setLoading(false);
					return;
				}

				if (!dashboard.url) {
					setError('Dashboard URL is not configured yet.');
					setLoading(false);
					return;
				}

				// 'internal' dashboards are Portal routes, not iframes — hand off to the router.
				if (dashboard.urlType === 'internal') {
					navigate(dashboard.url, { replace: true });
					return;
				}

				if (dashboard.urlType === 'external') {
					setDashboardUrl(getFullUrl(dashboard.url));
					return;
				}

				if (dashboard.urlType === 'iframe') {
					const headers = getAuthorazieHeader();

					const iframeResponse = await axios.get(
						`${url}${endpoints.xopsIntelligenceDashboards}/${dashboard.url}`,
						{ headers }
					);

					const resolvedUrl =
						iframeResponse.data?.data?.url ||
						iframeResponse.data?.url ||
						iframeResponse.data?.data;

					if (!resolvedUrl || typeof resolvedUrl !== 'string') {
						setError('Dashboard URL is not configured yet.');
						setLoading(false);
						return;
					}

					setDashboardUrl(getFullUrl(resolvedUrl));
				}
			} catch (err: any) {
				console.error('Failed to load dashboard:', err);

				if (err?.response?.status === 404) {
					setError('Dashboard not found.');
				} else {
					setError('Failed to load dashboard. Please try again later.');
				}

				setLoading(false);
			}
		};

		loadDashboard();
	}, [isEntitled, dashboard, navigate]);

	if (!isEntitled) {
		return <Navigate to="/LandingPage" replace />;
	}

	if (error) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
				}}
			>
				<Typography variant="body1" sx={{ color: 'error.main' }}>
					{error}
				</Typography>
			</Box>
		);
	}

	if (iframeError) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
				}}
			>
				<Typography variant="body1" sx={{ color: 'error.main' }}>
					Unable to load this dashboard. The dashboard may not allow embedding in PepWise.
				</Typography>
			</Box>
		);
	}

	// For iframe and external types, render in an iframe
	return (
		<Box
			sx={{
				width: '100%',
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				position: 'relative',
			}}
		>
			{loading && (
				<Box
					sx={{
						position: 'absolute',
						top: 0,
						left: 0,
						right: 0,
						bottom: 0,
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
						zIndex: 1,
					}}
				>
					<CircularProgress size={40} />
				</Box>
			)}

			{dashboardUrl && (
				<iframe
					src={dashboardUrl}
					title={dashboard?.dashboardName}
					style={{
						width: '100%',
						height: '100%',
						border: 'none',
						flexGrow: 1,
						marginTop: '40px',
						visibility: loading ? 'hidden' : 'visible',
					}}
					sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
					onLoad={() => {
						setLoading(false);
					}}
					onError={() => {
						setLoading(false);
						setIframeError(true);
					}}
				/>
			)}
		</Box>
	);
};

export default XOpsIntelligenceHost;
